import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PosicaoAtualAtivoItem } from '../../../../types/carteiraDiaria';
import { formatBRL, formatPercent, formatRentSimples } from '../../../../utils/formatters';
import { ExpectedReturn, calcExpectedReturn, formatCdiPct, formatCdiSpread } from '../../../../utils/expectedReturn';

export type CondominioFiltro = 'ABERTO' | 'FECHADO' | 'OUTROS' | null;

const GROUPS: {
  key: CondominioFiltro;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
}[] = [
  {
    key: 'ABERTO',
    label: 'Aberto',
    description: 'Fundos de condomínio aberto: permitem aplicações e resgates a qualquer momento (ou conforme regulamento). Exemplos: FI, FIC, ETF.',
    color: '#2C91AD', bgColor: '#EBF7FA', borderColor: '#9DD8E8',
  },
  {
    key: 'FECHADO',
    label: 'Fechado',
    description: 'Fundos de condomínio fechado: cotas não podem ser resgatadas antes do prazo de encerramento. Exemplos: FIDC, FIP, FII.',
    color: '#7B5EA7', bgColor: '#F3EFF9', borderColor: '#C4AEE8',
  },
  {
    key: 'OUTROS',
    label: 'Outros',
    description: 'Ativos sem classificação de condomínio. Inclui: ativos diretos (ações, títulos públicos, debêntures), fundos sem cadastro no sistema e fundos cadastrados mas com o campo "Tipo" ainda não preenchido.',
    color: '#6B7A8D', bgColor: '#F4F7F9', borderColor: '#CFD8E5',
  },
];

const classifyAtivo = (a: PosicaoAtualAtivoItem): CondominioFiltro => {
  if (a.condominio === 'ABERTO')  return 'ABERTO';
  if (a.condominio === 'FECHADO') return 'FECHADO';
  return 'OUTROS';
};

const rentColor = (v: number | null) => {
  if (v == null || v === 0) return '#8A98A8';
  return v > 0 ? '#29A152' : '#E51739';
};

/** Chip colorido que indica se o retorno realizado está acima, abaixo ou no alvo esperado. */
const DeltaChip: React.FC<{ delta: number | null }> = ({ delta }) => {
  if (delta == null) return null;
  const color   = delta > 0.00005 ? '#29A152' : delta < -0.00005 ? '#E51739' : '#8A98A8';
  const bgColor = delta > 0.00005 ? '#F0FBF4' : delta < -0.00005 ? '#FEF2F4' : '#F4F7F9';
  const Icon    = delta > 0.00005 ? ArrowDropUpIcon : delta < -0.00005 ? ArrowDropDownIcon : RemoveIcon;
  const label   = delta > 0.00005 ? 'Acima' : delta < -0.00005 ? 'Abaixo' : 'No alvo';
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.25,
      px: 0.75, py: 0.15, borderRadius: 1,
      backgroundColor: bgColor, border: `1px solid ${color}22`,
    }}>
      <Icon sx={{ fontSize: 13, color }} />
      <Typography sx={{ fontSize: '10px', fontWeight: 700, fontFamily: 'Inter', color }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color, ml: 0.25 }}>
        {formatRentSimples(delta)}
      </Typography>
    </Box>
  );
};

export interface CondominioBreakdownSectionProps {
  ativos: PosicaoAtualAtivoItem[];
  selected: CondominioFiltro;
  onSelect: (key: CondominioFiltro) => void;
  /** Segmento do fundo gerenciado (ex: "FIDC"). Controla exibição do retorno esperado. */
  segmento?: string | null;
  /** Taxas diárias dos indicadores, ex: { CDI: 0.000520, IPCA: 0.000021 }. */
  indicadoresDiarios?: Record<string, number> | null;
}

const CondominioBreakdownSection: React.FC<CondominioBreakdownSectionProps> = ({
  ativos, selected, onSelect, segmento, indicadoresDiarios,
}) => {
  const isFidc = segmento === 'FIDC';
  // Exibe coluna "Esperado" para FIDCs quando ao menos um indicador diário está disponível.
  const showExpected = isFidc && !!indicadoresDiarios;

  const groupData = useMemo(() => {
    return GROUPS.map(({ key, label, description, color, bgColor, borderColor }) => {
      const items = ativos.filter(a => classifyAtivo(a) === key);
      if (items.length === 0) return null;

      const concentracao       = items.reduce((s, a) => s + (a.concentracao ?? 0), 0);
      const valorFinanceiro    = items.reduce((s, a) => s + (a.valor_total ?? 0), 0);
      const hasVariacao        = items.some(a => a.variacao_financeira != null);
      const variacaoFinanceira = items.reduce((s, a) => s + (a.variacao_financeira ?? 0), 0);
      const plAntes            = valorFinanceiro - variacaoFinanceira;

      // ── Rentabilidade do segmento (Aberto / Fechado / Outros) ──────────────────
      //
      // Mesma lógica do AssetGroupCard, mas filtrada por tipo de condomínio.
      //
      //   rent_segmento = Σ (rent_i × valor_total_i) / Σ valor_total_i
      //
      // rent_i = rent_cota_corrigida = (cota_hoje − cota_ontem + amort_por_cota) / cota_ontem
      //
      // A amortização já está embutida em rent_cota_corrigida — não precisa de ajuste extra.
      // Fallback: variacao_financeira / PL_antes quando rent_cota_corrigida não está disponível.
      const itemsComCorrigida = items.filter(a => a.rent_cota_corrigida != null);
      const rentRealizada = itemsComCorrigida.length > 0
        ? itemsComCorrigida.reduce((s, a) => s + (a.rent_cota_corrigida! * (a.valor_total ?? 0)), 0)
          / itemsComCorrigida.reduce((s, a) => s + (a.valor_total ?? 0), 1)
        : hasVariacao && plAntes !== 0 ? variacaoFinanceira / plAntes : null;

      // Variação em R$: derivada do percentual corrigido para garantir consistência com rentRealizada.
      // var = rent × PL_hoje / (1 + rent)
      const varBrlCorrigida = rentRealizada != null
        ? rentRealizada * valorFinanceiro / (1 + rentRealizada)
        : null;

      let expectedReturn: ExpectedReturn | null = null;
      let expectedBrl: number | null = null;
      let delta: number | null = null;
      let ativosComExp = 0;

      if (showExpected && indicadoresDiarios) {
        const withExp = items
          .map(a => ({
            ret: calcExpectedReturn(a, indicadoresDiarios),
            val: a.valor_total ?? 0,
          }))
          .filter((x): x is { ret: ExpectedReturn; val: number } => x.ret !== null);

        ativosComExp = withExp.length;

        if (withExp.length > 0 && valorFinanceiro > 0) {
          // Pondera pelo valor total do grupo — ativos sem remuneração contribuem com zero.
          const daily = withExp.reduce((s, x) => s + x.ret.daily * x.val, 0) / valorFinanceiro;
          const cdiDiario = indicadoresDiarios['CDI'];
          const cdiPct = cdiDiario != null && cdiDiario !== 0 ? daily / cdiDiario : null;
          expectedReturn = { daily, cdiPct };
          expectedBrl = plAntes !== 0 ? daily * plAntes : null;
        }

        delta = rentRealizada != null && expectedReturn != null
          ? rentRealizada - expectedReturn.daily
          : null;
      }

      return {
        key, label, description, color, bgColor, borderColor,
        count: items.length, concentracao, valorFinanceiro,
        variacaoFinanceira, varBrlCorrigida, hasVariacao, rentRealizada,
        expectedReturn, expectedBrl, delta, ativosComExp,
      };
    }).filter((g): g is NonNullable<typeof g> => g !== null);
  }, [ativos, showExpected, indicadoresDiarios]);

  if (groupData.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {groupData.map(g => {
          const isSelected = selected === g.key;
          return (
            <Tooltip
              key={g.key}
              title={isSelected ? 'Clique para remover filtro' : `Filtrar por ${g.label}`}
              placement="top"
              arrow
            >
              <Paper
                elevation={0}
                onClick={() => onSelect(isSelected ? null : g.key)}
                sx={{
                  flex: '1 1 200px', minWidth: 200,
                  px: 2, py: 1.25, cursor: 'pointer',
                  border: `2px solid ${isSelected ? g.color : g.borderColor}`,
                  borderRadius: 2,
                  backgroundColor: isSelected ? g.bgColor : '#fff',
                  boxShadow: isSelected ? `0 0 0 3px ${g.color}22` : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { backgroundColor: g.bgColor, borderColor: g.color },
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: g.color, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1A2332', fontFamily: 'Inter', flex: 1 }}>
                    {g.label}
                  </Typography>
                  <Tooltip title={g.description} placement="top" arrow>
                    <InfoOutlinedIcon onClick={e => e.stopPropagation()} sx={{ fontSize: 14, color: '#B0BEC5', cursor: 'default', flexShrink: 0 }} />
                  </Tooltip>
                  <Box sx={{ px: 1, py: 0.1, borderRadius: 1, backgroundColor: g.color }}>
                    <Typography sx={{ fontSize: '11px', color: '#fff', fontWeight: 700, fontFamily: 'Inter' }}>{g.count}</Typography>
                  </Box>
                </Box>

                {/* Stats grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                  {/* Concentração */}
                  <Box>
                    <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Concentração
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A2332', fontFamily: 'Inter' }}>
                      {formatPercent(g.concentracao)}
                    </Typography>
                  </Box>

                  {/* Valor Financeiro */}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Valor Financeiro
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1A2332', fontFamily: 'Inter' }}>
                      {formatBRL(g.valorFinanceiro)}
                    </Typography>
                  </Box>

                  {/* Realizado */}
                  <Box>
                    <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Realizado
                    </Typography>
                    {g.hasVariacao ? (
                      <>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'Inter', color: rentColor(g.rentRealizada) }}>
                          {formatRentSimples(g.rentRealizada)}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: rentColor(g.varBrlCorrigida ?? g.variacaoFinanceira) }}>
                          {formatBRL(g.varBrlCorrigida ?? g.variacaoFinanceira)}
                        </Typography>
                        {formatCdiSpread(g.rentRealizada, indicadoresDiarios?.['CDI'] ?? null) && (
                          <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic' }}>
                            {formatCdiSpread(g.rentRealizada, indicadoresDiarios?.['CDI'] ?? null)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: '13px', color: '#8A98A8', fontFamily: 'Inter' }}>—</Typography>
                    )}
                  </Box>

                  {/* Esperado — visível apenas para FIDCs com indicadores disponíveis */}
                  {showExpected && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Tooltip
                        title={`${g.ativosComExp} de ${g.count} ativos com remuneração cadastrada`}
                        placement="top"
                        arrow
                      >
                        <Typography
                          onClick={e => e.stopPropagation()}
                          sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px', cursor: 'default', display: 'inline-block' }}
                        >
                          Esperado
                        </Typography>
                      </Tooltip>
                      {g.expectedReturn != null ? (
                        <>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'Inter', color: '#4D5766' }}>
                            {formatRentSimples(g.expectedReturn.daily)}
                          </Typography>
                          {g.expectedBrl != null && (
                            <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#6B7A8D' }}>
                              {formatBRL(g.expectedBrl)}
                            </Typography>
                          )}
                          {formatCdiPct(g.expectedReturn.cdiPct) && (
                            <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic' }}>
                              {formatCdiPct(g.expectedReturn.cdiPct)}
                            </Typography>
                          )}
                          {formatCdiSpread(g.expectedReturn.daily, indicadoresDiarios?.['CDI'] ?? null) && (
                            <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic' }}>
                              {formatCdiSpread(g.expectedReturn.daily, indicadoresDiarios?.['CDI'] ?? null)}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography sx={{ fontSize: '13px', color: '#8A98A8', fontFamily: 'Inter' }}>—</Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Delta chip — compara realizado vs esperado */}
                {showExpected && g.delta != null && (
                  <Box sx={{ mt: 0.75 }}>
                    <DeltaChip delta={g.delta} />
                  </Box>
                )}
              </Paper>
            </Tooltip>
          );
        })}
      </Box>

      {/* Aviso quando é FIDC mas indicadores não estão disponíveis para a data */}
      {isFidc && !indicadoresDiarios && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, px: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: '#F5A623' }} />
          <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: '#8A98A8' }}>
            Indicadores de mercado não disponíveis para esta data — retorno esperado não calculado.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CondominioBreakdownSection;
